/**
 * Menu flow — the top-level screen state machine.
 *
 *   boot → loading → menu → setup → game
 *                    ↕        ↕
 *                 settings  (back)
 *
 * In game, Esc opens the pause overlay (resume / settings / quit to menu).
 * Screens are DOM overlays owned by their own modules; this machine only
 * tracks which one is visible and routes the transitions. Pure logic —
 * no DOM here, so it's unit-testable.
 */
export type ScreenId =
  | "boot"
  | "loading"
  | "menu"
  | "setup"
  | "settings"
  | "game"
  | "pause";

export type MenuEvent =
  | { type: "loading-done" }
  | { type: "menu-skirmish" }
  | { type: "menu-settings" }
  | { type: "settings-back" }
  | { type: "setup-back" }
  | { type: "setup-start" }
  | { type: "game-pause" }
  | { type: "pause-resume" }
  | { type: "pause-settings" }
  | { type: "pause-quit-to-menu" };

const TRANSITIONS: Record<ScreenId, Partial<Record<MenuEvent["type"], ScreenId>>> = {
  boot: { "loading-done": "loading" },
  loading: { "loading-done": "menu" },
  menu: { "menu-skirmish": "setup", "menu-settings": "settings" },
  setup: { "setup-back": "menu", "setup-start": "game" },
  settings: { "settings-back": "menu" },
  game: { "game-pause": "pause" },
  pause: {
    "pause-resume": "game",
    "pause-settings": "settings",
    "pause-quit-to-menu": "menu",
  },
};

/** Where "back" from settings lands — menu normally, pause when opened there. */
function settingsBackTarget(from: ScreenId): ScreenId {
  return from === "pause" ? "pause" : "menu";
}

export class MenuFlow {
  private screen: ScreenId = "boot";
  /** Screen that opened settings (for the back target). */
  private settingsFrom: ScreenId = "menu";
  private listeners = new Set<(s: ScreenId) => void>();

  get(): ScreenId {
    return this.screen;
  }

  onChange(fn: (s: ScreenId) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private set(next: ScreenId): void {
    if (next === this.screen) return;
    this.screen = next;
    for (const fn of this.listeners) fn(next);
  }

  /** Returns true when the event caused a transition. */
  send(ev: MenuEvent): boolean {
    if (ev.type === "menu-settings" || ev.type === "pause-settings") {
      this.settingsFrom = this.screen;
      this.set("settings");
      return true;
    }
    if (ev.type === "settings-back") {
      this.set(settingsBackTarget(this.settingsFrom));
      return true;
    }
    const next = TRANSITIONS[this.screen][ev.type];
    if (!next) return false;
    this.set(next);
    return true;
  }
}
